import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Fade, IconButton, Typography, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { resourceSections } from '../../content/resourceSections';
import useResources from '../../hooks/useResources';
import ResourceCard from './ResourceCard';

function ArticleCarousel({ resources }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [index, setIndex] = useState(0);
    const [targetIndex, setTargetIndex] = useState(0);
    const [cardHeight, setCardHeight] = useState(null);
    const [paused, setPaused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [visible, setVisible] = useState(!document.hidden);
    const [inView, setInView] = useState(true);
    const container = useRef(null);
    const rotationControl = useRef(null);
    const card = useRef(null);
    const rotating = !paused && !reducedMotion;
    const changing = index !== targetIndex;
    // Animate the surrounding height too, including when a thumbnail loads.
    useLayoutEffect(() => {
        const measure = () => {
            const height = card.current?.getBoundingClientRect().height;
            if (height > 0) setCardHeight(height);
        };
        const observer = new ResizeObserver(measure);
        observer.observe(card.current);
        measure();
        return () => observer.disconnect();
    }, [index]);
    useLayoutEffect(() => {
        if (reducedMotion) setIndex(targetIndex);
    }, [reducedMotion, targetIndex]);
    useEffect(() => {
        const onVisibility = () => setVisible(!document.hidden);
        document.addEventListener('visibilitychange', onVisibility);
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
        observer.observe(container.current);
        return () => { observer.disconnect(); document.removeEventListener('visibilitychange', onVisibility); };
    }, []);
    useEffect(() => {
        if (!rotating || hovered || !visible || !inView) return;
        const timer = setInterval(() => setTargetIndex(current => (current + 1) % resources.length), 12000);
        return () => clearInterval(timer);
    }, [rotating, hovered, visible, inView, resources.length]);
    const move = delta => {
        if (changing) return;
        setPaused(true);
        setTargetIndex(current => (current + delta + resources.length) % resources.length);
    };
    return <Box ref={container} component="section" aria-labelledby="aging-well-heading" aria-roledescription="carousel"
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onFocusCapture={event => { if (event.target !== rotationControl.current) setPaused(true); }}
        sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography component="h2" variant="h5" id="aging-well-heading" sx={{ fontWeight: 700 }}>Aging Well</Typography>
            {!reducedMotion && <Button ref={rotationControl} onClick={() => setPaused(current => !current)} sx={{ minHeight: 48 }}>
                {rotating ? 'Pause articles' : 'Play articles'}
            </Button>}
        </Box>
        <Box aria-live={rotating ? 'off' : 'polite'} aria-atomic="true"
            sx={{ height: cardHeight ?? 'auto', overflow: 'hidden', transition: reducedMotion ? 'none' : 'height 300ms ease-in-out' }}>
            <Fade in={!changing} appear={false} timeout={reducedMotion ? 0 : { enter: 300, exit: 200 }} onExited={() => setIndex(targetIndex)}>
                <Box ref={card} inert={changing ? '' : undefined} aria-hidden={changing ? true : undefined}>
                    <ResourceCard key={resources[index].id} article={resources[index]} />
                </Box>
            </Fade>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton aria-label="Previous article" aria-disabled={changing} onClick={() => move(-1)} sx={{ minWidth: 48, minHeight: 48 }}><ArrowBackIcon /></IconButton>
                <Typography variant="body2">{index + 1} of {resources.length}</Typography>
                <IconButton aria-label="Next article" aria-disabled={changing} onClick={() => move(1)} sx={{ minWidth: 48, minHeight: 48 }}><ArrowForwardIcon /></IconButton>
            </Box>
            <Button component={Link} to="/resources" variant="outlined" sx={{ minHeight: 48 }}>Go to resources</Button>
        </Box>
    </Box>;
}

export function FeaturedResourcesContent({ resources }) {
    const { articles, sources, status, retry } = resources;
    if (status === 'loading') return null;
    // Alternate audiences so both appear early, preserving CDC's order within
    // each section. Content comes exclusively from the server's current list.
    const groups = resourceSections.map(section => articles.filter(article => article.category === section.id));
    const featured = [];
    for (let index = 0; index < Math.max(...groups.map(group => group.length)); index++) {
        for (const group of groups) if (group[index]) featured.push(group[index]);
    }
    if (featured.length) return <>
        <ArticleCarousel resources={featured} />
        {sources.some(source => source.stale) && <Typography variant="body2" color="text.secondary">Showing previously loaded CDC articles.</Typography>}
    </>;
    return <Box component="section" aria-labelledby="aging-well-heading" sx={{ mt: 3, mb: 2 }}>
        <Typography component="h2" variant="h5" id="aging-well-heading" sx={{ fontWeight: 700, mb: 2 }}>Aging Well</Typography>
        <Alert severity="info" action={<Button color="inherit" onClick={retry}>Retry</Button>}>Articles are temporarily unavailable.</Alert>
        <Button component={Link} to="/resources" variant="outlined" sx={{ mt: 2, minHeight: 48 }}>Go to resources</Button>
    </Box>;
}

export default function FeaturedResources() {
    const resources = useResources();
    return <FeaturedResourcesContent resources={resources} />;
}
